import * as React from 'react';
import {
  FieldPath,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Info,
  Plus,
  Trash2,
} from 'lucide-react';

import { Button } from 'components/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { MultiStopBetaTooltip } from 'pages/QuoteView/LoadBuilding/components/MultiStopBetaTooltip';
import { LoadStopsItem, LoadStopsItemTypes, NormalizedLoad } from 'types/Load';
import { cn } from 'utils/shadcn';

import StopForm from './Stop';
import {
  canMoveMGBStop,
  canRemoveMGBStop,
  createEmptyMGBStop,
  hydrateMGBCommodityStopFieldsFromForm,
  isMGBDropStopType,
  isMGBPickupStopType,
  isOnlyDropStopAt,
  isOnlyPickupStopAt,
  mgbStopLabel,
  remappedMGBCommodityStopIndicesAfterRemove,
} from './mgbStops';
import { isEmptyMGBCommodity, stopHasAssignedMGBFreight } from './utils';

type PendingDropFreightRestore = {
  /** Index in `commodities` at remove time (rows are not deleted on stop remove). */
  commodityIndex: number;
  description: string;
  referenceNumber: string;
  pieces: number;
  weightTotal: number;
};

function commodityDropRestoreFingerprint(c: {
  description?: string | null;
  referenceNumber?: string | null;
  totalPieces?: { val?: number | null } | null;
  quantity?: number | null;
  weightTotal?: number | null;
}): Omit<PendingDropFreightRestore, 'commodityIndex'> {
  return {
    description: c.description?.trim() ?? '',
    referenceNumber: c.referenceNumber?.trim() ?? '',
    pieces: Number(c.totalPieces?.val) || Number(c.quantity) || 0,
    weightTotal: Number(c.weightTotal) || 0,
  };
}

function matchesPendingDropFreight(
  c: Parameters<typeof commodityDropRestoreFingerprint>[0],
  pending: PendingDropFreightRestore
): boolean {
  const fp = commodityDropRestoreFingerprint(c);
  return (
    fp.description === pending.description &&
    fp.referenceNumber === pending.referenceNumber &&
    fp.pieces === pending.pieces &&
    fp.weightTotal === pending.weightTotal
  );
}

type RouteStopsProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  // Expand stop at this route index (e.g. after validation).
  activeStopTab?: string;
  // Bumped when validation requests a stop focus (re-opens even if tab id repeats).
  activeStopTabFocusKey?: number;
  // Called after the focus request is applied so parent can clear sticky tab state.
  onActiveStopTabHandled?: () => void;
  allowAddStops?: boolean;
};

function stopCollapsedSummary(stop: LoadStopsItem | undefined): string {
  if (!stop) {
    return '';
  }
  const name = stop.address?.name?.trim() ?? '';
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const place = [city, state].filter(Boolean).join(', ');
  if (name && place) {
    return `${name} · ${place}`;
  }
  return name || place;
}

function removeStopButtonTitle(args: {
  onlyPickupLeft: boolean;
  onlyDropLeft: boolean;
  breaksRouteTopology: boolean;
  hasAssignedFreight: boolean;
}): string {
  if (args.onlyPickupLeft) {
    return 'At least one pickup is required';
  }
  if (args.onlyDropLeft) {
    return 'At least one drop is required';
  }
  if (args.breaksRouteTopology) {
    return 'Cannot remove: route must start with a pickup and end with a drop';
  }
  if (args.hasAssignedFreight) {
    return 'Assigned freight will move to the remaining pickup or drop';
  }
  return 'Remove stop';
}

/**
 * Ordered multi-stop route editor: add / remove / move pickup & drop stops.
 * Uses `NormalizedLoad.stops` as source of truth; syncs first/last to pickup/consignee
 * on submit via `syncPickupConsigneeFromMGBStops`.
 */
export default function RouteStops({
  formMethods,
  activeStopTab,
  activeStopTabFocusKey = 0,
  onActiveStopTabHandled,
  allowAddStops = true,
}: RouteStopsProps): React.JSX.Element {
  const { control, getValues, setValue } = formMethods;
  const { fields, append, insert, remove, move } = useFieldArray({
    control,
    name: 'stops',
  });

  const stops = useWatch({ control, name: 'stops' }) ?? [];
  const commodities = useWatch({ control, name: 'commodities' }) ?? [];
  // Missing key → expanded (default so create flow still shows full forms).
  const [collapsedByFieldId, setCollapsedByFieldId] = React.useState<
    Record<string, boolean>
  >({});
  // Commodities that delivered to a removed drop — restore when that drop is re-added.
  const pendingDropFreightRestoreRef = React.useRef<
    PendingDropFreightRestore[] | null
  >(null);

  const isStopOpen = (fieldId: string) => collapsedByFieldId[fieldId] !== true;

  const setStopOpen = React.useCallback((fieldId: string, open: boolean) => {
    setCollapsedByFieldId((prev) => {
      if (open) {
        if (prev[fieldId] !== true) {
          return prev;
        }
        const { [fieldId]: _, ...rest } = prev;
        return rest;
      }
      if (prev[fieldId] === true) {
        return prev;
      }
      return { ...prev, [fieldId]: true };
    });
  }, []);

  // Drop collapsed state for removed field-array rows.
  React.useEffect(() => {
    setCollapsedByFieldId((prev) => {
      const fieldIds = new Set(fields.map((f) => f.id));
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const [id, collapsed] of Object.entries(prev)) {
        if (fieldIds.has(id)) {
          next[id] = collapsed;
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fields]);

  // Open a stop once per validation focus request — not on later add/remove/reorder.
  React.useEffect(() => {
    if (!activeStopTab?.startsWith('stop-') || !activeStopTabFocusKey) {
      return;
    }
    const index = Number(activeStopTab.slice('stop-'.length));
    if (!Number.isFinite(index) || index < 0 || index >= fields.length) {
      onActiveStopTabHandled?.();
      return;
    }
    const fieldId = fields[index]?.id;
    if (fieldId) {
      setStopOpen(fieldId, true);
    }
    onActiveStopTabHandled?.();
  }, [
    activeStopTab,
    activeStopTabFocusKey,
    fields,
    setStopOpen,
    onActiveStopTabHandled,
  ]);

  const incrementCommodityStopIndices = (fromIndex: number) => {
    const nextCommodities = hydrateMGBCommodityStopFieldsFromForm(
      getValues,
      getValues('commodities') ?? []
    ).map((c) => {
      if (!c) {
        return c;
      }

      let pickupStopNumber = c.pickupStopNumber;
      let dropStopNumber = c.dropStopNumber;

      if (pickupStopNumber != null && pickupStopNumber >= fromIndex) {
        pickupStopNumber = pickupStopNumber + 1;
      }
      if (dropStopNumber != null && dropStopNumber >= fromIndex) {
        dropStopNumber = dropStopNumber + 1;
      }

      return { ...c, pickupStopNumber, dropStopNumber };
    });
    setValue('commodities', nextCommodities, { shouldDirty: true });
    nextCommodities.forEach((c, i) => {
      if (!c) {
        return;
      }
      setValue(
        `commodities.${i}.pickupStopNumber` as FieldPath<NormalizedLoad>,
        c.pickupStopNumber as number,
        { shouldDirty: true }
      );
      setValue(
        `commodities.${i}.dropStopNumber` as FieldPath<NormalizedLoad>,
        c.dropStopNumber as number,
        { shouldDirty: true }
      );
    });
  };

  const reindexStopNumbers = () => {
    const next = getValues('stops') ?? [];
    next.forEach((_, i) => {
      setValue(`stops.${i}.stopNumber` as FieldPath<NormalizedLoad>, i, {
        shouldDirty: true,
      });
    });
  };

  const addStop = (
    type: LoadStopsItemTypes.Pickup | LoadStopsItemTypes.Dropoff
  ) => {
    if (!allowAddStops) {
      return;
    }
    const list = getValues('stops') ?? [];
    const isPickup = type === LoadStopsItemTypes.Pickup;

    if (isPickup) {
      // Insert before the final drop so the route still ends on a drop.
      const insertAt = Math.max(list.length - 1, 0);
      const newStop = createEmptyMGBStop(LoadStopsItemTypes.Pickup, insertAt);
      insert(insertAt, newStop);
      incrementCommodityStopIndices(insertAt);
      queueMicrotask(() => {
        setValue(
          `stops.${insertAt}.stopType` as FieldPath<NormalizedLoad>,
          LoadStopsItemTypes.Pickup,
          { shouldDirty: true }
        );
        reindexStopNumbers();
      });
      return;
    }

    const appendAt = list.length;
    const newStop = createEmptyMGBStop(LoadStopsItemTypes.Dropoff, appendAt);
    append(newStop);

    const pending = pendingDropFreightRestoreRef.current;
    if (pending && pending.length > 0) {
      const rows = [...(getValues('commodities') ?? [])];
      for (const entry of pending) {
        const c = rows[entry.commodityIndex];
        if (!c || isEmptyMGBCommodity(c)) {
          continue;
        }
        // Only restore this exact row — never match sibling lines that share
        // the same description/pickup (e.g. same SKU to different drops).
        if (!matchesPendingDropFreight(c, entry)) {
          continue;
        }
        rows[entry.commodityIndex] = { ...c, dropStopNumber: appendAt };
      }
      setValue('commodities', rows, { shouldDirty: true });
      pending.forEach((entry) => {
        const c = rows[entry.commodityIndex];
        if (!c || Number(c.dropStopNumber) !== appendAt) {
          return;
        }
        setValue(
          `commodities.${entry.commodityIndex}.dropStopNumber` as FieldPath<NormalizedLoad>,
          appendAt,
          { shouldDirty: true }
        );
      });
      pendingDropFreightRestoreRef.current = null;
    }

    queueMicrotask(() => {
      setValue(
        `stops.${appendAt}.stopType` as FieldPath<NormalizedLoad>,
        LoadStopsItemTypes.Dropoff,
        { shouldDirty: true }
      );
      reindexStopNumbers();
    });
  };

  const removeStopAt = (index: number) => {
    const list = getValues('stops') ?? [];
    if (!canRemoveMGBStop(list, index)) {
      return;
    }

    const commodities = hydrateMGBCommodityStopFieldsFromForm(
      getValues,
      getValues('commodities') ?? []
    );

    if (isMGBDropStopType(list[index]?.stopType)) {
      const pending: PendingDropFreightRestore[] = [];
      commodities.forEach((c, commodityIndex) => {
        if (
          !c ||
          isEmptyMGBCommodity(c) ||
          Number(c.dropStopNumber) !== index
        ) {
          return;
        }
        pending.push({
          commodityIndex,
          ...commodityDropRestoreFingerprint(c),
        });
      });
      pendingDropFreightRestoreRef.current = pending;
    } else {
      pendingDropFreightRestoreRef.current = null;
    }

    const remappedCommodities = remappedMGBCommodityStopIndicesAfterRemove(
      commodities,
      list,
      index
    );
    remove(index);
    setValue('commodities', remappedCommodities, { shouldDirty: true });
    remappedCommodities.forEach((c, i) => {
      if (!c) {
        return;
      }
      setValue(
        `commodities.${i}.pickupStopNumber` as FieldPath<NormalizedLoad>,
        c.pickupStopNumber as number,
        { shouldDirty: true }
      );
      setValue(
        `commodities.${i}.dropStopNumber` as FieldPath<NormalizedLoad>,
        c.dropStopNumber as number,
        { shouldDirty: true }
      );
    });
    queueMicrotask(() => reindexStopNumbers());
  };

  const moveStop = (from: number, to: number) => {
    const list = getValues('stops') ?? [];
    if (!canMoveMGBStop(list, from, to)) {
      return;
    }
    move(from, to);
    // Remap commodity stop indices after field-array move.
    const commodities = hydrateMGBCommodityStopFieldsFromForm(
      getValues,
      getValues('commodities') ?? []
    );
    const remapped = commodities.map((c) => {
      if (!c) {
        return c;
      }

      const remap = (n: number | null | undefined) => {
        if (n == null || !Number.isFinite(n)) {
          return n;
        }
        if (n === from) {
          return to;
        }
        if (from < to) {
          // Moving down: intermediate indices shift up.
          if (n > from && n <= to) {
            return n - 1;
          }
        } else {
          // Moving up: intermediate indices shift down.
          if (n >= to && n < from) {
            return n + 1;
          }
        }
        return n;
      };

      return {
        ...c,
        pickupStopNumber: remap(c.pickupStopNumber) as number | undefined,
        dropStopNumber: remap(c.dropStopNumber) as number | undefined,
      };
    });
    setValue('commodities', remapped, { shouldDirty: true });
    remapped.forEach((c, i) => {
      if (!c) {
        return;
      }
      setValue(
        `commodities.${i}.pickupStopNumber` as FieldPath<NormalizedLoad>,
        c.pickupStopNumber as number,
        { shouldDirty: true }
      );
      setValue(
        `commodities.${i}.dropStopNumber` as FieldPath<NormalizedLoad>,
        c.dropStopNumber as number,
        { shouldDirty: true }
      );
    });
    queueMicrotask(() => reindexStopNumbers());
  };

  return (
    <Flex direction='col' gap='md' className='w-full'>
      <Flex
        align='center'
        justify='between'
        className='w-full'
        wrap='wrap'
        gap='sm'
      >
        <Typography variant='body-sm' className='text-neutral-600'>
          {allowAddStops
            ? 'Add pickups and drops in the order the truck will run them. Reorder with the arrows. Assign each freight item to a pickup and drop in Specs.'
            : 'Reorder or remove pickups and drops in the order the truck will run them.'}
        </Typography>
        {allowAddStops ? (
          <Flex gap='xs' wrap='wrap' align='center'>
            <MultiStopBetaTooltip>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2 text-xs text-brand-500'
                buttonNamePosthog={null}
                onClick={() => addStop(LoadStopsItemTypes.Pickup)}
              >
                <Plus className='h-3.5 w-3.5 mr-1' />
                Add Pickup
              </Button>
            </MultiStopBetaTooltip>
            <MultiStopBetaTooltip>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2 text-xs text-brand-500'
                buttonNamePosthog={null}
                onClick={() => addStop(LoadStopsItemTypes.Dropoff)}
              >
                <Plus className='h-3.5 w-3.5 mr-1' />
                Add Drop
              </Button>
            </MultiStopBetaTooltip>
          </Flex>
        ) : null}
      </Flex>

      {!allowAddStops ? (
        <div
          role='status'
          className='w-full rounded border-l-4 border-info-500 bg-info-50 px-3 py-2'
        >
          <Flex align='start' gap='sm'>
            <Info
              className='mt-0.5 h-4 w-4 shrink-0 text-info-600'
              strokeWidth={2}
            />
            <Typography variant='body-xs' className='text-neutral-700'>
              New pickups and drops cannot be added after the load is created.
              Please add them directly in MercuryGate.
            </Typography>
          </Flex>
        </div>
      ) : null}

      {fields.map((field, index) => {
        const routeStopList =
          stops.length === fields.length ? stops : (getValues('stops') ?? []);

        const watchedStopType = routeStopList[index]?.stopType;
        const stopType =
          watchedStopType ??
          getValues(`stops.${index}.stopType` as FieldPath<NormalizedLoad>);

        const title = mgbStopLabel(routeStopList, index, {
          includeName: false,
        });
        const summary = stopCollapsedSummary(routeStopList[index]);
        const isOpen = isStopOpen(field.id);

        const onlyPickupLeft = isOnlyPickupStopAt(routeStopList, index);
        const onlyDropLeft = isOnlyDropStopAt(routeStopList, index);
        const canRemove = canRemoveMGBStop(routeStopList, index);
        const breaksRouteTopology =
          !canRemove && !onlyPickupLeft && !onlyDropLeft;
        const hasAssignedFreight = stopHasAssignedMGBFreight(
          commodities,
          index
        );

        const removeDisabled = !canRemove;
        const moveUpDisabled = !canMoveMGBStop(routeStopList, index, index - 1);
        const moveDownDisabled = !canMoveMGBStop(
          routeStopList,
          index,
          index + 1
        );

        return (
          <Collapsible
            key={field.id}
            open={isOpen}
            onOpenChange={(open) => setStopOpen(field.id, open)}
            className='w-full min-w-0 rounded-md border border-neutral-200 p-3'
          >
            <Flex
              align='start'
              justify='between'
              className='w-full'
              gap='sm'
              wrap='none'
            >
              <CollapsibleTrigger asChild>
                <Button
                  type='button'
                  variant='unstyled'
                  size='auto'
                  className='flex min-w-0 w-full flex-1 items-start justify-start gap-2 rounded-sm px-0 py-0 text-left font-normal hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300'
                  buttonNamePosthog={null}
                  aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
                >
                  <ChevronDown
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200',
                      !isOpen && '-rotate-90'
                    )}
                  />
                  <Flex direction='col' gap='none' className='min-w-0 flex-1'>
                    <Typography
                      variant='body-sm'
                      weight='semibold'
                      className='min-w-0 truncate'
                    >
                      {title}
                    </Typography>
                    {!isOpen && summary ? (
                      <Typography
                        variant='body-xs'
                        className='truncate text-neutral-500'
                      >
                        {summary}
                      </Typography>
                    ) : null}
                  </Flex>
                </Button>
              </CollapsibleTrigger>
              <Flex align='center' gap='xs' wrap='none' className='shrink-0'>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-8 px-2'
                  buttonNamePosthog={null}
                  title={
                    moveUpDisabled
                      ? 'Cannot move: route must start with a pickup'
                      : 'Move up'
                  }
                  disabled={moveUpDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveStop(index, index - 1);
                  }}
                >
                  <ArrowUp className='h-4 w-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-8 px-2'
                  buttonNamePosthog={null}
                  title={
                    moveDownDisabled
                      ? 'Cannot move: route must end with a drop'
                      : 'Move down'
                  }
                  disabled={moveDownDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveStop(index, index + 1);
                  }}
                >
                  <ArrowDown className='h-4 w-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-8 w-8 shrink-0 px-0 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 disabled:text-neutral-300'
                  buttonNamePosthog={null}
                  title={removeStopButtonTitle({
                    onlyPickupLeft,
                    onlyDropLeft,
                    breaksRouteTopology,
                    hasAssignedFreight,
                  })}
                  disabled={removeDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeStopAt(index);
                  }}
                >
                  <Trash2 className='h-4 w-4' />
                </Button>
              </Flex>
            </Flex>

            <CollapsibleContent
              forceMount
              hidden={!isOpen}
              className='min-w-0 w-full pt-3'
            >
              <StopForm
                stopKey={`stops.${index}` as `stops.${number}`}
                formMethods={formMethods}
                isPickup={isMGBPickupStopType(stopType)}
              />
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </Flex>
  );
}
